import { Reducer } from 'redux';
import Immutable from 'immutable';
import { combineEpics, Epic, ofType } from 'redux-observable';
import { catchError, debounceTime, map, pluck, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  IBidsResponse,
  IPurchasesRecord,
  MarketplaceActionTypes,
  purchaseActions,
  PurchaseActionTypes,
  PurchasesRecord,
} from '../interfaces';
import { OrderStatusEnum } from '../configs/order-statuses.enum';
import { isBiddingStatus, isWaitingStatus } from '../utils/status.utils';
import { addNotification } from 'modules/Notifications/duck';
import { PurchaseRepository } from '../repositories';
import { AjaxError } from 'rxjs/ajax';

export const purchases: Reducer<Immutable.Record<IPurchasesRecord>, purchaseActions> = (
  state = new PurchasesRecord(),
  action
) => {
  switch (action.type) {
    case PurchaseActionTypes.FETCH_GET_BIDS_SUCCESS: {
      const { meta, kits } = action.payload;

      const { ids, entities, ...computed } = kits.reduce(
        (acc, item) => {
          const bidding = isBiddingStatus(item.status);
          const waiting = isWaitingStatus(item.status);
          return {
            active: bidding || waiting ? acc.active + 1 : acc.active,
            wait: waiting ? acc.wait + 1 : acc.wait,
            cost: bidding || waiting ? acc.cost + item.price : acc.cost,
            ids:
              bidding || waiting
                ? [item.id.toString(), ...acc.ids]
                : [...acc.ids, item.id.toString()],
            entities: {
              ...acc.entities,
              [item.id]: item,
            },
          };
        },
        { active: 0, wait: 0, cost: 0, ids: [], entities: {} }
      );

      return state.set('meta', meta).set('computed', computed).set('kits', { ids, entities });
    }

    case PurchaseActionTypes.FETCH_CREATE_BID_SUCCESS: {
      const { id, ...rest } = action.payload;
      const updater = state
        .updateIn(['meta', 'amount'], amount => amount + 1)
        .updateIn(['kits', 'ids'], origin => [id.toString(), ...origin])
        .setIn(['kits', 'entities', id.toString()], { id, ...rest });

      return updater.update('computed', () => getComputed(updater.get('kits')));
    }

    case MarketplaceActionTypes.MARKETPLACE_PARTICIPANT_UPDATE: {
      const { id, ...rest } = action.payload;
      const identifier = id.toString();
      const item = state?.getIn(['kits', 'entities', identifier]);

      if (!state.hasIn(['kits', 'entities', identifier])) {
        return state;
      }

      const updater = state.mergeIn(['kits', 'entities', identifier], {
        ...rest,
        status:
          // Игнорируем auction-finished для участников - пользователь должен видеть обычный флоу торговли
          rest.status === OrderStatusEnum.AUCTION_FINISHED
            ? item.status
            : // Специальный случай: если текущий статус WAIT_FOR_TRADE и новый ORDER_CRATED, оставляем старый
            item.status === OrderStatusEnum.WAIT_FOR_TRADE &&
              rest.status === OrderStatusEnum.ORDER_CRATED
            ? item.status
            : rest.status || item.status,
      });

      return updater.update('computed', () => getComputed(updater.get('kits')));
    }

    case PurchaseActionTypes.FETCH_DELETE_BID_SUCCESS: {
      const id = action.payload.id.toString();
      const updater = state
        .updateIn(['meta', 'amount'], (amount: number) => amount - 1)
        .updateIn(['kits', 'ids'], (origin: string[]) => origin.filter((key: string) => key !== id))
        .deleteIn(['kits', 'entities', id]);

      return updater.update('computed', () => getComputed(updater.get('kits')));
    }

    default: {
      return state;
    }
  }
};

const getComputed = ({ ids, entities }: IPurchasesRecord['kits']) =>
  ids.reduce(
    (acc: IPurchasesRecord['computed'], id: string) => {
      const bidding = isBiddingStatus(entities[id].status);
      const waiting = isWaitingStatus(entities[id].status);
      return {
        active: bidding || waiting ? acc.active + 1 : acc.active,
        wait: waiting ? acc.wait + 1 : acc.wait,
        cost: bidding || waiting ? acc.cost + entities[id].price : acc.cost,
      };
    },
    { active: 0, wait: 0, cost: 0 }
  );

export const updateBids = (data: IBidsResponse) => ({
  type: PurchaseActionTypes.FETCH_GET_BIDS_SUCCESS,
  payload: data,
});

export const fetchCreateBid = (id: number) => {
  return {
    type: PurchaseActionTypes.FETCH_CREATE_BID,
    payload: {
      id,
    },
  };
};

const fetchCreateBidUpdate = (entity: any) => ({
  type: PurchaseActionTypes.FETCH_CREATE_BID_SUCCESS,
  payload: entity,
});

export const fetchDeleteBid = (id: number) => ({
  type: PurchaseActionTypes.FETCH_DELETE_BID,
  payload: {
    id,
  },
});

const fetchDeleteBidSuccess = (id: string) => ({
  type: PurchaseActionTypes.FETCH_DELETE_BID_SUCCESS,
  payload: {
    id,
  },
});

// Fetch current user bids on marketplace attach or explicit fetch action
export const fetchBidsEpic: Epic = action =>
  action.pipe(
    ofType(
      MarketplaceActionTypes.MARKETPLACE_ATTACH_LISTENERS as any,
      PurchaseActionTypes.FETCH_GET_BIDS as any
    ),
    switchMap(() =>
      PurchaseRepository.fetchBids().pipe(
        pluck('response'),
        map((response: IBidsResponse) =>
          updateBids(
            response ??
              (({ meta: { amount: 0, limit: 0, offset: 0 }, kits: [] } as unknown) as IBidsResponse)
          )
        ),
        catchError(() =>
          of(
            updateBids(({
              meta: { amount: 0, limit: 0, offset: 0 },
              kits: [],
            } as unknown) as IBidsResponse)
          )
        )
      )
    )
  );

const createBid: Epic = action$ =>
  action$.pipe(
    ofType(PurchaseActionTypes.FETCH_CREATE_BID),
    pluck('payload', 'id'),
    switchMap(id =>
      PurchaseRepository.fetchCreateBid(id).pipe(
        switchMap(({ response }) => {
          // response - это массив с одним элементом
          if (!response) {
            return of(addNotification({ type: 'error', body: 'Empty response from server' }));
          }

          const kitData = Array.isArray(response) ? response[0] : response;

          if (!kitData) {
            return of(addNotification({ type: 'error', body: 'Invalid kit data received' }));
          }

          // Обновляем данные, но НЕ устанавливаем auction-finished принудительно
          // Статус должен приходить от сервера через WebSocket
          const updatedKit = {
            ...kitData,
            // status: 'auction-finished', // УБИРАЕМ это
            isLastBidByUser: true,
            isUserSeller: false,
          };

          return of(
            fetchCreateBidUpdate(updatedKit),
            // Также обновляем store, чтобы диалог закрылся
            {
              type: MarketplaceActionTypes.MARKETPLACE_STORE_ITEMS_UPDATE,
              payload: { items: [updatedKit] },
            },
            addNotification({
              type: 'success',
              body: 'Your bid has been accepted',
            })
          );
        }),
        catchError((error: AjaxError) =>
          of(
            addNotification({
              type: 'error',
              body: error.response?.message || 'Failed to place bid',
            })
          )
        )
      )
    )
  );

const deletePurchase: Epic = action$ =>
  action$.pipe(
    ofType(PurchaseActionTypes.FETCH_DELETE_BID),
    debounceTime(500),
    pluck('payload', 'id'),
    switchMap(id =>
      PurchaseRepository.fetchDeleteBid(id).pipe(
        map(() => fetchDeleteBidSuccess(id.toString())),
        catchError(({ response }) => of(addNotification({ type: 'error', body: response.message })))
      )
    )
  );

export const purchaseEpics = combineEpics(fetchBidsEpic, createBid, deletePurchase);
